import React, { createContext, useContext, useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import { doc, getDoc } from 'firebase/firestore';
import { useAuth } from "./AuthContext";

const OrganizationContext = createContext();

export const OrganizationProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [institution, setinstitution] = useState(null); // Current organization
  const [associatedOrgs, setAssociatedOrgs] = useState([]); // List of organizations
  const [isOrgLoading, setIsOrgLoading] = useState(true); // Loading state for organization data
  const [needsOnboarding, setNeedsOnboarding] = useState(false); // Flag for setup requirement

  // Extract university domain from email
  const getUniversityDomain = (email) => {
    if (!email) return null;
    const domain = email.split('@')[1];
    return domain?.endsWith('.edu') ? domain : null;
  };

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!currentUser || !currentUser.email) {
        setinstitution(null);
        setAssociatedOrgs([]);
        setNeedsOnboarding(false);
        setIsOrgLoading(false);
        return;
      }

      try {
        setIsOrgLoading(true);

        // Get university domain from user's email
        const universityDomain = getUniversityDomain(currentUser.email);

        if (!universityDomain) {
          console.warn("User email is not from a university domain (.edu)");
          setinstitution(null);
          setAssociatedOrgs([]);
          setNeedsOnboarding(false);
          setIsOrgLoading(false);
          return;
        }

        // Fetch organization data from colleges collection using domain as ID
        const collegeRef = doc(db, "colleges", universityDomain);
        const collegeDoc = await getDoc(collegeRef);

        if (collegeDoc.exists()) {
          const collegeData = collegeDoc.data();

          // Create organization object from college data
          const organization = {
            id: universityDomain,
            domain: universityDomain,
            name: collegeData.name,
            fullName: collegeData.fullName,
            location: collegeData.location,
            colors: collegeData.colors || {
              primary: "#B0276E",
              secondary: "#F07458",
              accent: "#FFEEA6"
            },
            features: collegeData.features || {
              dining: true,
              events: true,
              courses: true,
              networking: true
            },
            status: collegeData.status || 'active'
          };

          setAssociatedOrgs([organization]);
          setinstitution(organization);
          setNeedsOnboarding(false);

        } else {
          console.warn(`No college configuration found for domain: ${universityDomain}`);
          setinstitution(null);
          setAssociatedOrgs([]);
          setNeedsOnboarding(true); // Set onboarding flag instead of navigating
        }

      } catch (error) {
        console.error("Error fetching organization:", error);
        setinstitution(null);
        setAssociatedOrgs([]);
        setNeedsOnboarding(false);
      } finally {
        setIsOrgLoading(false);
      }
    };

    fetchOrganizations();
  }, [currentUser]);

  // Switch organization (for future multi-org support)
  const switchOrg = (orgId) => {
    const org = associatedOrgs.find(o => o.id === orgId);
    if (org) {
      setinstitution(org);
    }
  };

  // Helper function to check if a feature is enabled
  const isFeatureEnabled = (featureName) => {
    return institution?.features?.[featureName] ?? false;
  };

  // Helper function to get organization colors
  const getOrgColors = () => {
    return institution?.colors || {
      primary: "#B0276E",
      secondary: "#F07458",
      accent: "#FFEEA6"
    };
  };

  const value = {
    institution,
    associatedOrgs,
    isOrgLoading,
    needsOnboarding,
    switchOrg,
    isFeatureEnabled,
    getOrgColors,
    universityDomain: institution?.domain || null,
    universityName: institution?.name || null,
    universityFullName: institution?.fullName || null,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
};